/**
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React from 'react';
import { Helmet } from 'react-helmet';
import { Switch, Route } from 'react-router-dom';
import { Layout } from 'antd';

import Header from 'containers/Header';
import Sider from 'containers/Sider';
import Footer from 'containers/Footer';
import GlobalStyle from 'globals/styles';
import { createLoadableComponent } from 'utils/loadable';
import createProtectedComponentWithSpecialisationCheck from 'utils/auth';
import createComponentWithSpecialisationCheck from 'utils/specialisation';

import { FlexGrowDiv, ColorLayoutContent } from './styles';
import './theme.css';

const OverallHome = createLoadableComponent(() => import('pages/OverallHome'));
const Home = createLoadableComponent(() => import('pages/Home'));
const Login = createLoadableComponent(() => import('pages/Login'));
const ChangePassword = createLoadableComponent(() =>
  import('pages/ChangePassword'),
);
const ResetPassword = createLoadableComponent(() =>
  import('pages/ResetPassword'),
);
const PrivilegedLogin = createLoadableComponent(() =>
  import('pages/PrivilegedLogin'),
);
const ForgetPassword = createLoadableComponent(() =>
  import('pages/ForgetPassword'),
);
const Admin = createLoadableComponent(() => import('pages/Admin'));
const AdminManageLecturer = createLoadableComponent(() =>
  import('pages/AdminManageLecturer'),
);
const AdminAddNewLecturer = createLoadableComponent(() =>
  import('pages/AdminAddNewLecturer'),
);
const AdminAddNewLecturersWithExcel = createLoadableComponent(() =>
  import('pages/AdminAddNewLecturersWithExcel'),
);
const AdminEditLecturer = createLoadableComponent(() =>
  import('pages/AdminEditLecturer'),
);
const AdminManageStudent = createLoadableComponent(() =>
  import('pages/AdminManageStudent'),
);
const AdminAddNewStudentsWithExcel = createLoadableComponent(() =>
  import('pages/AdminAddNewStudentsWithExcel'),
);
const AdminAddNewStudent = createLoadableComponent(() =>
  import('pages/AdminAddNewStudent'),
);
const AdminEditStudent = createLoadableComponent(() =>
  import('pages/AdminEditStudent'),
);
const AdminManageSetting = createLoadableComponent(() =>
  import('pages/AdminManageSetting'),
);
const AdminStartNewAcademicSession = createLoadableComponent(() =>
  import('pages/AdminStartNewAcademicSession'),
);
const AdminChangeAcademicSession = createLoadableComponent(() =>
  import('pages/AdminChangeAcademicSession'),
);

const Coordinator = createLoadableComponent(() => import('pages/Coordinator'));
const CoordinatorMainConfig = createLoadableComponent(() =>
  import('pages/CoordinatorManageMainConfig'),
);
const CoordinatorManageProjectCategory = createLoadableComponent(() =>
  import('pages/CoordinatorManageProjectCategory'),
);
const CoordinatorAddNewProjectCategory = createLoadableComponent(() =>
  import('pages/CoordinatorAddNewProjectCategory'),
);
const CoordinatorEditProjectCategory = createLoadableComponent(() =>
  import('pages/CoordinatorEditProjectCategory'),
);
const CoordinatorManageGradeEvaluation = createLoadableComponent(() =>
  import('pages/CoordinatorManageGradeEvaluation'),
);
const CoordinatorManageBiddingProcess = createLoadableComponent(() =>
  import('pages/CoordinatorManageBiddingProcess'),
);
const CoordinatorAssignRole = createLoadableComponent(() =>
  import('pages/CoordinatorAssignRole'),
);
const CoordinatorViewProjects = createLoadableComponent(() =>
  import('pages/CoordinatorViewProjects'),
);
const CoordinatorViewProjectInfo = createLoadableComponent(() =>
  import('pages/CoordinatorViewProjectInfo'),
);
const CoordinatorViewProjectCounts = createLoadableComponent(() =>
  import('pages/CoordinatorViewProjectCounts'),
);
const CoordinatorViewStudentProjectSupervisor = createLoadableComponent(() =>
  import('pages/CoordinatorViewStudentProjectSupervisor'),
);
const CoordinatorViewBiddingStatus = createLoadableComponent(() =>
  import('pages/CoordinatorViewBiddingStatus'),
);
const CoordinatorViewProjectsGrades = createLoadableComponent(() =>
  import('pages/CoordinatorViewProjectsGrades'),
);
const CoordinatorViewProjectGrades = createLoadableComponent(() =>
  import('pages/CoordinatorViewProjectGrades'),
);
const CoordinatorEditEvaluation = createLoadableComponent(() =>
  import('pages/CoordinatorEditEvaluation'),
);
const CoordinatorResetAcademicSession = createLoadableComponent(() =>
  import('pages/CoordinatorResetAcademicSession'),
);

const Lecturer = createLoadableComponent(() => import('pages/Lecturer'));
const LecturerManageProfile = createLoadableComponent(() =>
  import('pages/LecturerManageProfile'),
);
const LecturerManageProject = createLoadableComponent(() =>
  import('pages/LecturerManageProject'),
);
const LecturerViewStudentProfile = createLoadableComponent(() =>
  import('pages/LecturerViewStudentProfile'),
);
const LecturerAddProject = createLoadableComponent(() =>
  import('pages/LecturerAddProject'),
);
const LecturerEditProject = createLoadableComponent(() =>
  import('pages/LecturerEditProject'),
);
const LecturerViewOtherProject = createLoadableComponent(() =>
  import('pages/LecturerViewOtherProject'),
);
const LecturerViewOtherProjectInfo = createLoadableComponent(() =>
  import('pages/LecturerViewOtherProjectInfo'),
);
const LecturerBidProject = createLoadableComponent(() =>
  import('pages/LecturerBidProject'),
);
const LecturerEvaluateGradeAsSupervisor = createLoadableComponent(() =>
  import('pages/LecturerEvaluateGradeAsSupervisor'),
);
const LecturerEditEvaluationAsSupervisor = createLoadableComponent(() =>
  import('pages/LecturerEditEvaluationAsSupervisor'),
);
const LecturerEvaluateGradeAsExaminer = createLoadableComponent(() =>
  import('pages/LecturerEvaluateGradeAsExaminer'),
);
const LecturerEditEvaluationAsExaminer = createLoadableComponent(() =>
  import('pages/LecturerEditEvaluationAsExaminer'),
);

const Student = createLoadableComponent(() => import('pages/Student'));
const StudentManageProjectOffer = createLoadableComponent(() =>
  import('pages/StudentManageProjectOffer'),
);
const StudentViewProjectInfo = createLoadableComponent(() =>
  import('pages/StudentViewProjectInfo'),
);
const StudentManageBiddedProjects = createLoadableComponent(() =>
  import('pages/StudentManageBiddedProjects'),
);
const StudentEditBiddedProjects = createLoadableComponent(() =>
  import('pages/StudentEditBiddedProjects'),
);
const StudentManageProfile = createLoadableComponent(() =>
  import('pages/StudentManageProfile'),
);
const StudentManageGroup = createLoadableComponent(() =>
  import('pages/StudentManageGroup'),
);

const StudentViewProjectEvaluation = createLoadableComponent(() =>
  import('pages/StudentViewProjectEvaluation'),
);
const NotFound = createLoadableComponent(() => import('pages/NotFound'));

function App() {
  return (
    <>
      <Helmet
        titleTemplate="%s - Smart Project Management System"
        defaultTitle="Smart Project Management System"
      >
        <meta
          name="description"
          content="A Final Year Project Bidding System"
        />
      </Helmet>
      <Header />
      <FlexGrowDiv>
        <Layout>
          <Sider />
          <ColorLayoutContent>
            <Switch>
              <Route exact path="/" component={OverallHome} />
              <Route
                exact
                path="/:specialisation/"
                component={createComponentWithSpecialisationCheck(Home)}
              />
              <Route
                exact
                path="/:specialisation/users/login"
                component={createComponentWithSpecialisationCheck(Login)}
              />
              <Route
                exact
                path="/:specialisation/users/privilegedlogin"
                component={createComponentWithSpecialisationCheck(
                  PrivilegedLogin,
                )}
              />
              <Route
                exact
                path="/:specialisation/users/firstlogin/:userId"
                component={createComponentWithSpecialisationCheck(
                  ChangePassword,
                )}
              />
              <Route
                exact
                path="/:specialisation/users/forgetpassword"
                component={createComponentWithSpecialisationCheck(
                  ForgetPassword,
                )}
              />
              <Route
                exact
                path="/:specialisation/users/resetpassword/:userId/:resetId"
                component={createComponentWithSpecialisationCheck(
                  ResetPassword,
                )}
              />

              <Route
                exact
                path="/:specialisation/admin"
                component={createProtectedComponentWithSpecialisationCheck(
                  Admin,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/changepassword"
                component={createProtectedComponentWithSpecialisationCheck(
                  ChangePassword,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/lecturer"
                component={createProtectedComponentWithSpecialisationCheck(
                  AdminManageLecturer,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/lecturer/addnewlecturer"
                component={createProtectedComponentWithSpecialisationCheck(
                  AdminAddNewLecturer,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/lecturer/addlecturerswithexcel"
                component={createProtectedComponentWithSpecialisationCheck(
                  AdminAddNewLecturersWithExcel,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/lecturer/:lecturerid"
                component={createProtectedComponentWithSpecialisationCheck(
                  AdminEditLecturer,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/student"
                component={createProtectedComponentWithSpecialisationCheck(
                  AdminManageStudent,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/student/addnewstudentswithexcel"
                component={createProtectedComponentWithSpecialisationCheck(
                  AdminAddNewStudentsWithExcel,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/student/addnewstudent"
                component={createProtectedComponentWithSpecialisationCheck(
                  AdminAddNewStudent,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/student/:studentid"
                component={createProtectedComponentWithSpecialisationCheck(
                  AdminEditStudent,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/coordinator/resetpassword"
                component={createProtectedComponentWithSpecialisationCheck(
                  ResetPassword,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/setting/"
                component={createProtectedComponentWithSpecialisationCheck(
                  AdminManageSetting,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/setting/startnewacademicsession"
                component={createProtectedComponentWithSpecialisationCheck(
                  AdminStartNewAcademicSession,
                  ['admin'],
                )}
              />
              <Route
                exact
                path="/:specialisation/admin/setting/changeacademicsession"
                component={createProtectedComponentWithSpecialisationCheck(
                  AdminChangeAcademicSession,
                  ['admin'],
                )}
              />

              <Route
                exact
                path="/:specialisation/coordinator"
                component={createProtectedComponentWithSpecialisationCheck(
                  Coordinator,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/changepassword"
                component={createProtectedComponentWithSpecialisationCheck(
                  ChangePassword,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/mainconfig"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorMainConfig,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/projectcategory/"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorManageProjectCategory,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/projectcategory/addnewprojectcategory"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorAddNewProjectCategory,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/projectcategory/:projectcategoryid"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorEditProjectCategory,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/gradeevaluation"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorManageGradeEvaluation,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/biddingprocess"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorManageBiddingProcess,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/roleassignment"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorAssignRole,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/viewprojects"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorViewProjects,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/viewprojects/:projectId"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorViewProjectInfo,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/projectcount"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorViewProjectCounts,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/studentprojectsupervisor"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorViewStudentProjectSupervisor,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/biddingstatus"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorViewBiddingStatus,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/projectsgrades"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorViewProjectsGrades,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/projectsgrades/:projectId"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorViewProjectGrades,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/projectsgrades/:projectId/:examinerNumber"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorEditEvaluation,
                  ['coordinator'],
                )}
              />
              <Route
                exact
                path="/:specialisation/coordinator/reset"
                component={createProtectedComponentWithSpecialisationCheck(
                  CoordinatorResetAcademicSession,
                  ['coordinator'],
                )}
              />

              <Route
                exact
                path="/:specialisation/lecturer"
                component={createProtectedComponentWithSpecialisationCheck(
                  Lecturer,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/changepassword"
                component={createProtectedComponentWithSpecialisationCheck(
                  ChangePassword,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/myprofile"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerManageProfile,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/manageproject"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerManageProject,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/manageproject/addproject/"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerAddProject,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/manageproject/:projectid"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerEditProject,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/viewotherproject"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerViewOtherProject,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/viewotherproject/:projectId"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerViewOtherProjectInfo,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/projectbidding/"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerBidProject,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/projectbidding/:studentId"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerViewStudentProfile,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/supervisorevaluation/"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerEvaluateGradeAsSupervisor,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/supervisorevaluation/:projectId"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerEditEvaluationAsSupervisor,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/examinerevaluation/"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerEvaluateGradeAsExaminer,
                  ['lecturer'],
                )}
              />
              <Route
                exact
                path="/:specialisation/lecturer/examinerevaluation/:projectId"
                component={createProtectedComponentWithSpecialisationCheck(
                  LecturerEditEvaluationAsExaminer,
                  ['lecturer'],
                )}
              />

              <Route
                exact
                path="/:specialisation/student"
                component={createProtectedComponentWithSpecialisationCheck(
                  Student,
                  ['student'],
                )}
              />
              <Route
                exact
                path="/:specialisation/student/changepassword"
                component={createProtectedComponentWithSpecialisationCheck(
                  ChangePassword,
                  ['student'],
                )}
              />
              <Route
                exact
                path="/:specialisation/student/myprofile"
                component={createProtectedComponentWithSpecialisationCheck(
                  StudentManageProfile,
                  ['student'],
                )}
              />
              <Route
                exact
                path="/:specialisation/student/mygroup"
                component={createProtectedComponentWithSpecialisationCheck(
                  StudentManageGroup,
                  ['student'],
                )}
              />
              <Route
                exact
                path="/:specialisation/student/projectoffer"
                component={createProtectedComponentWithSpecialisationCheck(
                  StudentManageProjectOffer,
                  ['student'],
                )}
              />
              <Route
                exact
                path="/:specialisation/student/projectoffer/:projectId"
                component={createProtectedComponentWithSpecialisationCheck(
                  StudentViewProjectInfo,
                  ['student'],
                )}
              />
              <Route
                exact
                path="/:specialisation/student/projectbidding"
                component={createProtectedComponentWithSpecialisationCheck(
                  StudentManageBiddedProjects,
                  ['student'],
                )}
              />
              <Route
                exact
                path="/:specialisation/student/projectbidding/edit"
                component={createProtectedComponentWithSpecialisationCheck(
                  StudentEditBiddedProjects,
                  ['student'],
                )}
              />
              <Route
                exact
                path="/:specialisation/student/projectevaluation"
                component={createProtectedComponentWithSpecialisationCheck(
                  StudentViewProjectEvaluation,
                  ['student'],
                )}
              />

              <Route path="" component={NotFound} />
            </Switch>
          </ColorLayoutContent>
        </Layout>
      </FlexGrowDiv>
      <Footer />
      <GlobalStyle />
    </>
  );
}

export default App;
